import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {Skeleton} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {useCompactP2Context} from '../compactP2Context';
import {CellsFailed} from './cellsFailed';

/*
 * Props.
 */

interface CellsLoaderProps {
  status: AsyncStatusesEnum;
  onRetryClick: () => void;
  inboxSupportsRefinements: boolean;
  hasSearchInInboxesParameters: boolean;
}

/*
 * Style.
 */

const StyledNewLoaderDiv = styled.div<{$isCompact: boolean}>`
  grid-row: 1 / span 3;
  grid-column: 1;
  min-width: 0;
  pointer-events: none;
  display: grid;
  align-content: flex-start;
  grid-auto-flow: row;
  grid-auto-rows: auto;
  grid-row-gap: 8px;
  padding: 13px 14px;
  padding-right: 8px;

  & > * {
    pointer-events: all;
  }

  ${(p) =>
    p.$isCompact &&
    css`
      grid-row-gap: 2px;
    `};
`;

const StyledHeaderDiv = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledRightSideHeaderDiv = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledCompactSkeleton = styled(Skeleton)<{$isFirst?: boolean}>`
  ${(p) =>
    p.$isFirst &&
    css`
      margin-top: 5px;
    `}
`;

/*
 * Component.
 */

export const CellsLoader: FC<CellsLoaderProps> = (props) => {
  const {status, onRetryClick} = props;
  const {isCompact} = useCompactP2Context();
  const renderSkeletons = () => {
    if (isCompact) {
      return (
        <>
          <StyledCompactSkeleton $isFirst borderRadius="8px 8px 0 0" width="100%" height="36px" />
          <StyledCompactSkeleton borderRadius="0" width="100%" height="36px" />
          <StyledCompactSkeleton borderRadius="0" width="100%" height="36px" />
          <StyledCompactSkeleton borderRadius="0" width="100%" height="36px" />
          <StyledCompactSkeleton borderRadius="0" width="100%" height="36px" />
          <StyledCompactSkeleton borderRadius="0" width="100%" height="36px" />
          <StyledCompactSkeleton borderRadius="0 0 8px 8px" width="100%" height="36px" />
        </>
      );
    }

    return (
      <>
        <Skeleton borderRadius="8px" width="100%" height="84px" />
        <Skeleton borderRadius="8px" width="100%" height="84px" />
        <Skeleton borderRadius="8px" width="100%" height="84px" />
      </>
    );
  };

  if (status !== AsyncStatusesEnum.FAILED) {
    return (
      <StyledNewLoaderDiv $isCompact={isCompact}>
        {renderHeader(props)}
        {renderSkeletons()}
      </StyledNewLoaderDiv>
    );
  }

  return <CellsFailed onRetryClick={onRetryClick} />;
};

function renderHeader(props: CellsLoaderProps) {
  const {inboxSupportsRefinements, hasSearchInInboxesParameters} = props;

  if (hasSearchInInboxesParameters) {
    return (
      <StyledRightSideHeaderDiv>
        <Skeleton borderRadius="4px" width="90px" height="20px" />
      </StyledRightSideHeaderDiv>
    );
  }

  if (inboxSupportsRefinements) {
    return (
      <StyledHeaderDiv>
        <Skeleton borderRadius="4px" width="90px" height="20px" />
        <Skeleton borderRadius="4px" width="90px" height="20px" />
      </StyledHeaderDiv>
    );
  }
  return (
    <StyledRightSideHeaderDiv>
      <Skeleton borderRadius="4px" width="90px" height="20px" />
    </StyledRightSideHeaderDiv>
  );
}
